import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, KeyRound, Mail, Smartphone, Check, AlertTriangle, Copy } from "lucide-react";

export default function Security() {
  const [status, setStatus] = useState({ totp_enabled: false, email_enabled: false });
  const [setup, setSetup] = useState(null); // { qr_png_base64, secret, provisioning_uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startTotp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/totp/setup");
      setSetup(data);
      setCode("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verifyTotp = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/totp/verify", { code });
      toast.success("Authenticator app connected — 2FA is now ON for your account");
      setSetup(null); setCode(""); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disableTotp = async () => {
    if (!confirm("Turn off authenticator app 2FA?")) return;
    try { await api.post("/auth/2fa/totp/disable"); toast.success("Authenticator app 2FA disabled"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleEmail = async (on) => {
    try {
      await api.post(on ? "/auth/2fa/email/enable" : "/auth/2fa/email/disable");
      toast.success(on ? "Email 2FA enabled" : "Email 2FA disabled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copySecret = () => { navigator.clipboard.writeText(setup.secret); toast.success("Secret copied"); };

  const anyOn = status.totp_enabled || status.email_enabled;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="security-root">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <Shield className="h-3.5 w-3.5" /> Account security
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Two-Factor Authentication</h1>
        <p className="text-sm text-slate-500 mt-1">Add an extra layer beyond your password. Pick either or both methods.</p>
      </div>

      <Card className={`p-4 rounded-xl border-slate-200/60 ${anyOn ? "bg-emerald-50/40" : "bg-amber-50/40"} border`}>
        <div className="flex items-center gap-3">
          {anyOn ? <Check className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
          <div className="text-sm text-slate-700">
            {anyOn ? <>Two-factor is <strong>ON</strong>. You'll be asked for a code on next sign-in.</> : <>Two-factor is currently <strong>OFF</strong>. We recommend enabling at least one method.</>}
          </div>
        </div>
      </Card>

      {/* TOTP */}
      <Card className="p-5 rounded-xl border-slate-200/60">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 grid place-items-center"><KeyRound className="h-5 w-5 text-[#DC2626]" /></div>
            <div>
              <div className="font-semibold text-slate-900">Authenticator app (TOTP)</div>
              <div className="text-xs text-slate-500">Google Authenticator, Microsoft Authenticator, 1Password, Authy…</div>
            </div>
          </div>
          {status.totp_enabled
            ? <div className="flex items-center gap-2"><Badge className="bg-emerald-50 text-emerald-700 border-0">Enabled</Badge><Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={disableTotp} data-testid="disable-totp">Disable</Button></div>
            : <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={startTotp} disabled={busy} data-testid="setup-totp">Set up</Button>}
        </div>
      </Card>

      {/* Email 2FA */}
      <Card className="p-5 rounded-xl border-slate-200/60">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 grid place-items-center"><Mail className="h-5 w-5 text-[#DC2626]" /></div>
            <div>
              <div className="font-semibold text-slate-900">Email one-time code</div>
              <div className="text-xs text-slate-500">A 6-digit code is emailed to your registered work address (via Microsoft 365).</div>
            </div>
          </div>
          <Switch checked={status.email_enabled} onCheckedChange={toggleEmail} data-testid="toggle-email-2fa" />
        </div>
      </Card>

      {/* TOTP setup dialog */}
      <Dialog open={!!setup} onOpenChange={() => setSetup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="h-4 w-4" /> Scan with your authenticator app</DialogTitle></DialogHeader>
          {setup && (
            <div className="space-y-4">
              <div className="grid place-items-center bg-white p-4 border rounded-lg">
                <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="TOTP QR" className="h-52 w-52" data-testid="totp-qr" />
              </div>
              <div className="text-xs">
                <div className="text-slate-500 mb-1">Can't scan? Enter this secret manually:</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono bg-slate-50 p-2 rounded border text-slate-800 text-xs break-all">{setup.secret}</code>
                  <Button size="sm" variant="outline" onClick={copySecret}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div>
                <Label>Enter the 6-digit code from your app</Label>
                <Input value={code} inputMode="numeric" maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-12 text-center tracking-[0.5em] font-mono text-lg" data-testid="totp-verify-code" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetup(null)}>Cancel</Button>
                <Button onClick={verifyTotp} disabled={busy || code.length < 6} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="totp-verify-submit">Enable 2FA</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
